import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  ConversationNodeLoadAction,
  ConversationNodeLoadSucceededAction,
  ConversationNodeLoadErroredAction,
  FocusedConversationNodeLoadAction,
  FocusedConversationNodeLoadSucceededAction,
  FocusedConversationNodeLoadErroredAction,
} from './conversation-node.actions';
import {
  ConversationNodeApiError,
  ConversationNodeApiErrorTypes,
} from './conversation-node.errors';
import { ConversationNodeService } from './conversation-node.service';
import { Store } from '@ngrx/store';
import { add, format } from 'date-fns';
import { AppStore } from '../store.model';
import { getConversationNodeFilter } from './conversation-node.selectors';
import { LoadInsights } from '../insights/insights.actions';
import { isFeatureFlagEnabled } from '../feature-flags/feature-flags.selectors';
import { ConversationNodesSummaryFormats } from './types';

@Injectable()
export class ConversationNodeEffects {
  loadedInsightProject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadInsights),
      withLatestFrom(this.store.select(isFeatureFlagEnabled('insights-conversation-nodes'))),
      filter(([, isEnabled]) => isEnabled),
      map(([{ projectId }]) =>
        ConversationNodeLoadAction({
          filter: {
            projectId,
            startdate: format(add(new Date(), { years: -1 }), 'yyyy-MM-dd'),
            format: ConversationNodesSummaryFormats.Graph,
          },
        }),
      ),
    ),
  );

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ConversationNodeLoadAction),
      switchMap(({ filter: actionFilter }) =>
        this.conversationNode.load(actionFilter).pipe(
          map(({ links, data }) =>
            ConversationNodeLoadSucceededAction({
              seriesLinks: links,
              seriesData: data,
            }),
          ),
          catchError((error) =>
            of(
              ConversationNodeLoadErroredAction({
                apiError: new ConversationNodeApiError(
                  ConversationNodeApiErrorTypes.LoadTopicalFlowError,
                  error,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadFocusedNode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FocusedConversationNodeLoadAction),
      withLatestFrom(this.store.select(getConversationNodeFilter)),
      switchMap(([, conversationNodeFilter]) =>
        this.conversationNode.load(conversationNodeFilter).pipe(
          map(({ links, data }) =>
            FocusedConversationNodeLoadSucceededAction({
              seriesLinks: links,
              seriesData: data,
            }),
          ),
          catchError((error) =>
            of(
              FocusedConversationNodeLoadErroredAction({
                apiError: new ConversationNodeApiError(
                  ConversationNodeApiErrorTypes.LoadTopicalFlowError,
                  error,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  constructor(
    private conversationNode: ConversationNodeService,
    private actions$: Actions,
    private store: Store<AppStore>,
  ) {}
}
